import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { toastify } from '../../../../../utils/toast';
import { useFacilityStore } from '../../../../../app/z-store/store';
import {
  addCommonAreaGeneratorReadingUrl,
  updateCommonAreaGeneratorReadingURL,
  getCommonAreaGeneratorReadingsUrl
} from '../../../../../utils/urls';

const GeneratorTab = () => {
  const [generatorData, setGeneratorData] = useState([]);
  const [allGeneratorData, setAllGeneratorData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [generatorDialogVisible, setGeneratorDialogVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedGeneratorItem, setSelectedGeneratorItem] = useState(null);
  const [generatorForm, setGeneratorForm] = useState({
    openingMinutes: 0,
    closingHours: 0,
    closingMinutes: 0,
    totalReadingHours: 0,
    totalReadingMinutes: 0,
    totalFuelLevelFraction: '',
    totalFuelLevelLiters: '',
    refillAmountLiters: '',
    date: new Date().toISOString().split('T')[0]
  });

  const facilityId = useFacilityStore((state) => state.facilityId);

  useEffect(() => {
    if (facilityId) {
      fetchGeneratorData();
    }
  }, [facilityId]);

  useEffect(() => {
    const openingReadingMinutes = hoursAndMinutesToMinutes(
      generatorForm.openingHours,
      generatorForm.openingMinutes
    );

    const currentReadingMinutes = hoursAndMinutesToMinutes(
      generatorForm.totalReadingHours,
      generatorForm.totalReadingMinutes
    );

    if (openingReadingMinutes !== undefined && currentReadingMinutes !== undefined) {
      const closingReadingMinutes = openingReadingMinutes + currentReadingMinutes;
      const { hours: closingHours, minutes: closingMins } = minutesToHoursAndMinutes(closingReadingMinutes);

      setGeneratorForm(prev => ({
        ...prev,
        closingHours,
        closingMinutes: closingMins
      }));
    }
  }, [
    generatorForm.openingHours,
    generatorForm.openingMinutes,
    generatorForm.totalReadingHours,
    generatorForm.totalReadingMinutes
  ]);

  const fetchGeneratorData = async () => {
    if (!facilityId) return;

    setLoading(true);
    try {
      const response = await makeRequest2(
        `${getCommonAreaGeneratorReadingsUrl}/${facilityId}`,
        'GET'
      );

      if (
        response.success &&
        response.data &&
        response.data.readings
      ) {
        const readings = response.data.readings;
        const data = Array.isArray(readings) ? readings : [readings];

        const sortedData = [...data].sort((a, b) => new Date(b.date) - new Date(a.date));

        const processedData = sortedData.map(item => {
          const totalHrsRunDecimal = item.totalHrsRun || 0;
          const totalHrsRunHours = Math.floor(totalHrsRunDecimal);
          const totalHrsRunMinutes = Math.round((totalHrsRunDecimal - totalHrsRunHours) * 60);

          return {
            ...item,
            totalHrsRunHours,
            totalHrsRunMinutes
          };
        });

        setAllGeneratorData(processedData);
        setGeneratorData(processedData);
      }
    } catch (error) {
      toastify("Failed to load generator data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Reset form values
  const resetGeneratorForm = () => {
    setGeneratorForm({
      openingHours: 0,
      openingMinutes: 0,
      closingHours: 0,
      closingMinutes: 0,
      totalReadingHours: 0,
      totalReadingMinutes: 0,
      totalFuelLevelFraction: '',
      totalFuelLevelLiters: '',
      refillAmountLiters: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const filterDataByDateRange = () => {
    if (!startDate && !endDate) {
      setGeneratorData(allGeneratorData);
      return;
    }

    const filteredData = allGeneratorData.filter(item => {
      const itemDate = new Date(item.date);

      if (startDate && !endDate) {
        return itemDate >= startDate;
      }
      if (!startDate && endDate) {
        return itemDate <= endDate;
      }

      return itemDate >= startDate && itemDate <= endDate;
    });

    setGeneratorData(filteredData);
  };

  const minutesToHoursAndMinutes = (totalMinutes) => {
    if (totalMinutes === undefined || totalMinutes === null) return { hours: 0, minutes: 0 };

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return { hours, minutes };
  };

  const hoursAndMinutesToMinutes = (hours, minutes) => {
    return (parseInt(hours || 0) * 60) + parseInt(minutes || 0);
  };

  const getLatestClosingReading = () => {
    if (!allGeneratorData || allGeneratorData.length === 0) {
      return { hours: 0, minutes: 0 };
    }

    const latestEntry = allGeneratorData[0];

    if (latestEntry && latestEntry.closingReading !== undefined) {
      return minutesToHoursAndMinutes(latestEntry.closingReading);
    }

    return { hours: 0, minutes: 0 };
  };

  const openAddDialog = () => {
    setIsEditMode(false);
    resetGeneratorForm();

    const { hours, minutes } = getLatestClosingReading();

    setGeneratorForm(prev => ({
      ...prev,
      openingHours: hours,
      openingMinutes: minutes,
      date: new Date().toISOString().split('T')[0]
    }));

    setGeneratorDialogVisible(true);
  };

  const openEditDialog = (item) => {
    setIsEditMode(true);
    setSelectedGeneratorItem(item);

    const openingReading = minutesToHoursAndMinutes(item.openingReading);
    const closingReading = minutesToHoursAndMinutes(item.closingReading);

    setGeneratorForm({
      openingHours: openingReading.hours,
      openingMinutes: openingReading.minutes,
      closingHours: closingReading.hours,
      closingMinutes: closingReading.minutes,
      totalReadingHours: closingReading.hours,
      totalReadingMinutes: closingReading.minutes,
      totalFuelLevelFraction: item.totalFuelLevelFraction || '',
      totalFuelLevelLiters: item.totalFuelLevelLiters?.toString() || '',
      refillAmountLiters: item.refillAmountLiters ? item.refillAmountLiters.toString() : '',
      date: new Date(item.date).toISOString().split('T')[0]
    });

    setGeneratorDialogVisible(true);
  };

  const handleGeneratorSave = async () => {
    const openingReading = hoursAndMinutesToMinutes(generatorForm.openingHours, generatorForm.openingMinutes);
    const closingReading = hoursAndMinutesToMinutes(generatorForm.closingHours, generatorForm.closingMinutes);

    const totalHrsRun = parseFloat((generatorForm.totalReadingHours + (generatorForm.totalReadingMinutes / 60)).toFixed(2));

    if (openingReading === undefined || closingReading === undefined ||
      !generatorForm.totalFuelLevelLiters) {
      toastify("Please fill all required fields", "error");
      return;
    }

    let fuelLevelFraction = generatorForm.totalFuelLevelFraction;
    if (fuelLevelFraction && !fuelLevelFraction.endsWith('%')) {
      fuelLevelFraction = `${fuelLevelFraction}%`;
    }

    setLoading(true);
    try {
      const payload = {
        date: generatorForm.date,
        openingReading,
        closingReading,
        totalHrsRun,
        totalFuelLevelFraction: fuelLevelFraction,
        totalFuelLevelLiters: parseFloat(generatorForm.totalFuelLevelLiters),
        refillAmountLiters: generatorForm.refillAmountLiters ? parseFloat(generatorForm.refillAmountLiters) : undefined
      };

      let response;
      if (isEditMode && selectedGeneratorItem) {
        response = await makeRequest2(
          `${updateCommonAreaGeneratorReadingURL}/${facilityId}/${selectedGeneratorItem._id}`,
          "PUT",
          payload
        );
      } else {
        // Add new record
        response = await makeRequest2(
          `${addCommonAreaGeneratorReadingUrl}/${facilityId}`,
          "POST",
          payload
        );
      }

      if (response.success) {
        toastify(`Generator reading ${isEditMode ? 'updated' : 'added'} successfully`, "success");
        setGeneratorDialogVisible(false);
        resetGeneratorForm();
        fetchGeneratorData();
      } else {
        toastify(response.message || `Failed to ${isEditMode ? 'update' : 'add'} generator reading`, "error");
      }
    } catch (error) {
      toastify(`Error ${isEditMode ? 'updating' : 'adding'} generator reading`, "error");
    } finally {
      setLoading(false);
    }
  };

  const formatTimeDisplay = (totalMinutes) => {
    if (totalMinutes === undefined || totalMinutes === null) return '';

    const { hours, minutes } = minutesToHoursAndMinutes(totalMinutes);
    return `${hours}h ${minutes}m`;
  };

  const formatHoursRunDisplay = (rowData) => {
    if (rowData.totalHrsRunHours === undefined || rowData.totalHrsRunMinutes === undefined) return '';
    return `${rowData.totalHrsRunHours}h ${rowData.totalHrsRunMinutes}m`;
  };

  const generatorActionTemplate = (rowData) => {
    return (
      <div>
        <Button
          icon="ti ti-pencil"
          className="p-button-rounded p-button-info"
          onClick={() => openEditDialog(rowData)}
          tooltip="Edit Generator Reading"
        />
      </div>
    );
  };

  const formatDate = (rowData) => {
    if (!rowData.date) return '';
    const date = new Date(rowData.date);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  const generatorDialogFooter = (
    <div className="text-end mt-4">
      <Button
        type="button"
        className="btn btn-outline-secondary"
        onClick={() => setGeneratorDialogVisible(false)}
        disabled={loading}
      >
        Cancel
      </Button>
      &nbsp;
      <Button
        type="button"
        className="btn btn-primary"
        onClick={handleGeneratorSave}
        disabled={loading}
      >
        {loading ? "Saving..." : "Save"}
      </Button>
    </div>
  );

  const openingReadingTemplate = (rowData) => {
    return formatTimeDisplay(rowData.openingReading);
  };

  const closingReadingTemplate = (rowData) => {
    return formatTimeDisplay(rowData.closingReading);
  };

  const fuelLevelTemplate = (rowData) => {
    if (!rowData.totalFuelLevelFraction && !rowData.totalFuelLevelLiters) return '';

    if (rowData.totalFuelLevelFraction && rowData.totalFuelLevelLiters) {
      return `${rowData.totalFuelLevelFraction} (${rowData.totalFuelLevelLiters}L)`;
    }

    return rowData.totalFuelLevelFraction || `${rowData.totalFuelLevelLiters}L`;
  };

  const getMaxDate = () => {
    return new Date();
  };

  return (
    <>
      <div className="mb-4">
        <div className="card">
          <div className="card-body d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <span className="me-2">Date Range:</span>
              <Calendar
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.value)}
                showIcon
                placeholder="Start Date"
                className="me-2"
              />
              <span className="me-2">to</span>
              <Calendar
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.value)}
                showIcon
                placeholder="End Date"
                className="me-2"
              />
              <Button
                icon="ti ti-search"
                className="ms-2"
                onClick={filterDataByDateRange}
                tooltip="Search"
              />
              <Button
                icon="ti ti-refresh"
                className="ms-2"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                  setGeneratorData(allGeneratorData);
                }}
                tooltip="Reset"
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={openAddDialog}
            >
              Add Generator Reading
            </button>
          </div>
        </div>
      </div>
      <DataTable
        value={generatorData}
        loading={loading}
        paginator
        rows={10}
        emptyMessage="No generator data found"
        stripedRows
        sortMode="multiple"
        removableSort
        tableStyle={{ minWidth: '50rem' }}
      >
        <Column body={openingReadingTemplate} header="Opening Reading" sortable field="openingReading" />
        <Column body={closingReadingTemplate} header="Closing Reading" sortable field="closingReading" />
        <Column body={formatHoursRunDisplay} header="Total Hours Run" sortable field="totalHrsRun" />
        <Column body={fuelLevelTemplate} header="Fuel Level" sortable field="totalFuelLevelLiters" />
        <Column field="refillAmountLiters" header="Refill Amount (L)" sortable />
        <Column body={formatDate} header="Date" sortable field="date" />
        <Column header="Actions" body={generatorActionTemplate} />
      </DataTable>

      {/* Generator Dialog */}
      <Dialog
        visible={generatorDialogVisible}
        style={{ width: '500px' }}
        header={isEditMode ? "Edit Generator Reading" : "Add Generator Reading"}
        modal
        className="p-fluid"
        footer={generatorDialogFooter}
        onHide={() => setGeneratorDialogVisible(false)}
      >
        {/* Opening Reading with Hours and Minutes inputs - Read-only when adding new record */}
        <div className="field">
          <label htmlFor="openingReading">Opening Reading (Hours & Minutes)</label>
          <div className="p-inputgroup">
            <InputNumber
              id="openingHours"
              value={generatorForm.openingHours}
              onValueChange={(e) => setGeneratorForm({ ...generatorForm, openingHours: e.value })}
              min={0}
              placeholder="Hours"
              suffix=" hrs"
              style={{ width: '50%' }}
              readOnly={!isEditMode}
            />
            <InputNumber
              id="openingMinutes"
              value={generatorForm.openingMinutes}
              onValueChange={(e) => setGeneratorForm({ ...generatorForm, openingMinutes: e.value })}
              min={0}
              max={59}
              placeholder="Minutes"
              suffix=" mins"
              style={{ width: '50%' }}
              readOnly={!isEditMode}
            />
          </div>
          {!isEditMode && <small className="text-muted">Auto-filled with latest closing reading</small>}
        </div>

        {/* Current Total Reading (hours & minutes from generator panel) */}
        <div className="field">
          <label htmlFor="totalReading">Current Reading from Generator Panel (Hours & Minutes)</label>
          <div className="p-inputgroup">
            <InputNumber
              id="totalReadingHours"
              value={generatorForm.totalReadingHours}
              onValueChange={(e) => setGeneratorForm({ ...generatorForm, totalReadingHours: e.value })}
              min={0}
              placeholder="Hours"
              suffix=" hrs"
              style={{ width: '50%' }}
            />
            <InputNumber
              id="totalReadingMinutes"
              value={generatorForm.totalReadingMinutes}
              onValueChange={(e) => setGeneratorForm({ ...generatorForm, totalReadingMinutes: e.value })}
              min={0}
              max={59}
              placeholder="Minutes"
              suffix=" mins"
              style={{ width: '50%' }}
            />
          </div>
        </div>

        {/* Closing Reading with Hours and Minutes inputs - Read-only (auto-calculated) */}
        <div className="field">
          <label htmlFor="closingReading">Closing Reading (Hours & Minutes)</label>
          <div className="p-inputgroup">
            <InputNumber
              id="closingHours"
              value={generatorForm.closingHours}
              onValueChange={(e) => setGeneratorForm({ ...generatorForm, closingHours: e.value })}
              min={0}
              placeholder="Hours"
              suffix=" hrs"
              style={{ width: '50%' }}
              readOnly={true}
            />
            <InputNumber
              id="closingMinutes"
              value={generatorForm.closingMinutes}
              onValueChange={(e) => setGeneratorForm({ ...generatorForm, closingMinutes: e.value })}
              min={0}
              max={59}
              placeholder="Minutes"
              suffix=" mins"
              style={{ width: '50%' }}
              readOnly={true}
            />
          </div>
          <small className="text-muted">Auto-set based on the current reading from generator panel</small>
        </div>

        {/* Fuel Level Percentage */}
        <div className="field">
          <label htmlFor="totalFuelLevelFraction">Fuel Level (Percentage)</label>
          <InputNumber
            id="totalFuelLevelFraction"
            value={generatorForm.totalFuelLevelFraction ? generatorForm.totalFuelLevelFraction.replace('%', '') : ''}
            onValueChange={(e) => setGeneratorForm({ ...generatorForm, totalFuelLevelFraction: e.value ? `${e.value}%` : '' })}
            suffix="%"
            min={0}
            max={100}
            placeholder="Enter percentage"
          />
        </div>

        {/* Fuel Level in Liters */}
        <div className="field">
          <label htmlFor="totalFuelLevelLiters">Fuel Level (Liters)</label>
          <InputNumber
            id="totalFuelLevelLiters"
            value={generatorForm.totalFuelLevelLiters}
            onValueChange={(e) => setGeneratorForm({ ...generatorForm, totalFuelLevelLiters: e.value })}
            placeholder="Fuel level in liters"
            min={0}
            suffix=" L"
            required
          />
        </div>

        {/* Refill Amount in Liters */}
        <div className="field">
          <label htmlFor="refillAmountLiters">Refill Amount (Liters)</label>
          <InputNumber
            id="refillAmountLiters"
            value={generatorForm.refillAmountLiters}
            onValueChange={(e) => setGeneratorForm({ ...generatorForm, refillAmountLiters: e.value })}
            placeholder="Leave empty if no refill"
            min={0}
            suffix=" L"
          />
        </div>

        {/* Date - with max date set to today */}
        <div className="field">
          <label htmlFor="date">Date</label>
          <Calendar
            id="date"
            value={new Date(generatorForm.date)}
            onChange={(e) => setGeneratorForm({ ...generatorForm, date: e.value.toISOString().split('T')[0] })}
            maxDate={getMaxDate()}
            showIcon
            dateFormat="yy-mm-dd"
            required
          />
        </div>
      </Dialog>
    </>
  );
};

export default GeneratorTab;